import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '@/stores';
import { useToast } from '@/components/common/Toast';
import { Button } from '@/components/common/Button';
import { ConfirmModal } from '@/components/common/ConfirmModal';
import {
  Users, UserPlus, KeyRound, ShieldCheck, UserCheck, Trash2,
  Lock, Check, AlertCircle, Loader2, RefreshCw, Eye, EyeOff,
  Copy, ShieldAlert, Sparkles
} from 'lucide-react';
import { clsx } from 'clsx';

interface UserItem {
  id: string;
  username: string;
  name: string;
  role: 'admin' | 'staff';
  createdAt: string | null;
}

export function UsersManager() {
  const { user: currentUser, token } = useAuthStore();
  const { showToast } = useToast();

  const [users, setUsers] = useState<UserItem[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // User deletion state
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [deletingUser, setDeletingUser] = useState(false);

  // New user form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [newRole, setNewRole] = useState<'admin' | 'staff'>('staff');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showNewPasswordText, setShowNewPasswordText] = useState(false);
  const [creating, setCreating] = useState(false);

  // Reset password for a user
  const [resetTargetUser, setResetTargetUser] = useState<string | null>(null);
  const [resetPasswordVal, setResetPasswordVal] = useState('');
  const [confirmResetPasswordVal, setConfirmResetPasswordVal] = useState('');
  const [showResetPasswordText, setShowResetPasswordText] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Change self password state
  const [currentPass, setCurrentPass] = useState('');
  const [newSelfPass, setNewSelfPass] = useState('');
  const [confirmSelfPass, setConfirmSelfPass] = useState('');
  const [showSelfPassText, setShowSelfPassText] = useState(false);
  const [changingSelfPass, setChangingSelfPass] = useState(false);

  // Recovery Key state (Admin)
  const [recoveryKey, setRecoveryKey] = useState<string>('');
  const [showRecoveryKey, setShowRecoveryKey] = useState(false);
  const [loadingRecoveryKey, setLoadingRecoveryKey] = useState(false);
  const [copiedRecoveryKey, setCopiedRecoveryKey] = useState(false);
  const [regeneratingKey, setRegeneratingKey] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);

  const fetchUsers = useCallback(async () => {
    if (!token || currentUser?.role !== 'admin') return;
    setLoadingUsers(true);
    try {
      const res = await fetch('/api/admin/users', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setUsers(data.data || []);
      } else {
        showToast(data.error || 'Error al cargar usuarios', 'error');
      }
    } catch {
      showToast('Error de conexión al cargar usuarios', 'error');
    } finally {
      setLoadingUsers(false);
    }
  }, [token, currentUser?.role, showToast]);

  const fetchRecoveryKey = useCallback(async () => {
    if (!token || currentUser?.role !== 'admin') return;
    setLoadingRecoveryKey(true);
    try {
      const res = await fetch('/api/admin/recovery-key', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setRecoveryKey(data.data?.recoveryKey || '');
      }
    } catch {
      // Ignore background error
    } finally {
      setLoadingRecoveryKey(false);
    }
  }, [token, currentUser?.role]);

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    fetchUsers();
    // oxlint-disable-next-line react/set-state-in-effect
    fetchRecoveryKey();
  }, [fetchUsers, fetchRecoveryKey]);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUsername.trim() || !newPassword.trim()) {
      showToast('Usuario y contraseña son requeridos', 'error');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      showToast('La contraseña y su confirmación no coinciden', 'error');
      return;
    }

    if (newPassword.length < 4) {
      showToast('La contraseña debe tener al menos 4 caracteres', 'error');
      return;
    }

    setCreating(true);
    try {
      const res = await fetch('/api/admin/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          username: newUsername.trim(),
          name: newName.trim() || newUsername.trim(),
          role: newRole,
          password: newPassword.trim(),
        }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        showToast(`Usuario '${newUsername}' creado exitosamente`, 'success');
        setNewName('');
        setNewUsername('');
        setNewPassword('');
        setConfirmNewPassword('');
        setNewRole('staff');
        setShowCreateForm(false);
        fetchUsers();
      } else {
        showToast(data.error || 'Error al crear usuario', 'error');
      }
    } catch {
      showToast('Error de conexión', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleResetPassword = async (username: string) => {
    if (!resetPasswordVal.trim() || resetPasswordVal.length < 4) {
      showToast('La nueva contraseña debe tener al menos 4 caracteres', 'error');
      return;
    }

    if (resetPasswordVal !== confirmResetPasswordVal) {
      showToast('La nueva contraseña y su confirmación no coinciden', 'error');
      return;
    }

    setResetting(true);
    try {
      const res = await fetch('/api/admin/users', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          username,
          password: resetPasswordVal.trim(),
        }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        showToast(`Contraseña de '${username}' actualizada`, 'success');
        setResetTargetUser(null);
        setResetPasswordVal('');
        setConfirmResetPasswordVal('');
      } else {
        showToast(data.error || 'Error al restablecer contraseña', 'error');
      }
    } catch {
      showToast('Error de conexión', 'error');
    } finally {
      setResetting(false);
    }
  };

  const handleDeleteUser = (username: string) => {
    setUserToDelete(username);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    setDeletingUser(true);
    try {
      const res = await fetch(`/api/admin/users?username=${encodeURIComponent(userToDelete)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (res.ok && data.success) {
        showToast(data.message || 'Usuario eliminado', 'success');
        setUserToDelete(null);
        fetchUsers();
      } else {
        showToast(data.error || 'Error al eliminar usuario', 'error');
      }
    } catch {
      showToast('Error de conexión', 'error');
    } finally {
      setDeletingUser(false);
    }
  };

  const handleChangeSelfPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPass || !newSelfPass) {
      showToast('Ingresa tu contraseña actual y la nueva', 'error');
      return;
    }

    if (newSelfPass !== confirmSelfPass) {
      showToast('La nueva contraseña y su confirmación no coinciden', 'error');
      return;
    }

    if (newSelfPass.length < 4) {
      showToast('La nueva contraseña debe tener al menos 4 caracteres', 'error');
      return;
    }

    setChangingSelfPass(true);
    try {
      const res = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPassword: currentPass,
          newPassword: newSelfPass,
        }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        showToast('¡Tu contraseña ha sido actualizada con éxito!', 'success');
        setCurrentPass('');
        setNewSelfPass('');
        setConfirmSelfPass('');
      } else {
        showToast(data.error || 'Error al cambiar contraseña', 'error');
      }
    } catch {
      showToast('Error de conexión', 'error');
    } finally {
      setChangingSelfPass(false);
    }
  };

  const handleCopyRecoveryKey = async () => {
    if (!recoveryKey) return;
    try {
      await navigator.clipboard.writeText(recoveryKey);
      setCopiedRecoveryKey(true);
      showToast('Código de rescate copiado al portapapeles', 'success');
      setTimeout(() => setCopiedRecoveryKey(false), 2500);
    } catch {
      showToast('Error al copiar código', 'error');
    }
  };

  const handleRegenerateRecoveryKey = () => {
    setShowRegenerateConfirm(true);
  };

  const confirmRegenerateRecoveryKey = async () => {
    setRegeneratingKey(true);
    try {
      const res = await fetch('/api/admin/recovery-key', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setRecoveryKey(data.data?.recoveryKey);
        setShowRegenerateConfirm(false);
        showToast('Nuevo código de rescate generado. ¡Guárdalo en un lugar seguro!', 'success');
      } else {
        showToast(data.error || 'Error al generar código', 'error');
      }
    } catch {
      showToast('Error de conexión', 'error');
    } finally {
      setRegeneratingKey(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* 1. SECCIÓN: GESTIÓN DE ACCESOS Y USUARIOS (ADMIN) */}
      {currentUser?.role === 'admin' ? (
        <div className="bg-surface border border-border rounded-xl p-4 space-y-4 shadow-xs">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-brand-primary/10 text-brand-primary flex items-center justify-center shrink-0">
                <Users size={16} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-text-primary">Usuarios & Accesos</h3>
                <p className="text-[11px] text-text-muted">Crea y gestiona cuentas para el personal</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={fetchUsers}
                disabled={loadingUsers}
                className="p-1.5 text-text-muted hover:text-text-primary rounded-lg bg-surface-secondary border border-border cursor-pointer transition-colors"
                title="Recargar usuarios"
              >
                <RefreshCw size={13} className={clsx(loadingUsers && 'animate-spin')} />
              </button>
              <Button
                type="button"
                size="sm"
                variant={showCreateForm ? 'secondary' : 'primary'}
                onClick={() => setShowCreateForm(!showCreateForm)}
              >
                <UserPlus size={14} />
                <span>{showCreateForm ? 'Cancelar' : 'Nuevo Acceso'}</span>
              </Button>
            </div>
          </div>

          {/* Formulario de Creación */}
          {showCreateForm && (
            <form onSubmit={handleCreateUser} className="bg-surface-secondary p-3.5 rounded-xl border border-border space-y-3 animate-fade-in">
              <div className="text-xs font-semibold text-text-primary flex items-center gap-1.5">
                <UserPlus size={14} className="text-brand-primary" />
                <span>Crear Nuevo Usuario / Acceso</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-[11px] font-medium text-text-secondary">Nombre o Descripción</label>
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Ej: Recepción Tarde"
                    className="w-full px-2.5 py-1.5 bg-surface border border-border rounded-lg text-xs"
                    required
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] font-medium text-text-secondary">Nombre de Usuario (Login)</label>
                  <input
                    type="text"
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    placeholder="Ej: recepcion"
                    autoCapitalize="none"
                    className="w-full px-2.5 py-1.5 bg-surface border border-border rounded-lg text-xs font-mono"
                    required
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] font-medium text-text-secondary">Rol de Acceso</label>
                  <select
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value as 'admin' | 'staff')}
                    className="w-full px-2.5 py-1.5 bg-surface border border-border rounded-lg text-xs"
                  >
                    <option value="staff">Personal de Atención (Acceso operativo diario)</option>
                    <option value="admin">Administrador (Control total)</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="text-[11px] font-medium text-text-secondary">Contraseña</label>
                    <button
                      type="button"
                      onClick={() => setShowNewPasswordText(!showNewPasswordText)}
                      className="text-[10px] text-text-muted hover:text-text-primary flex items-center gap-0.5"
                    >
                      {showNewPasswordText ? <EyeOff size={11} /> : <Eye size={11} />}
                      <span>{showNewPasswordText ? 'Ocultar' : 'Ver'}</span>
                    </button>
                  </div>
                  <input
                    type={showNewPasswordText ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Mínimo 4 caracteres"
                    className="w-full px-2.5 py-1.5 bg-surface border border-border rounded-lg text-xs"
                    required
                  />
                </div>

                <div className="space-y-1 sm:col-span-2">
                  <label className="text-[11px] font-medium text-text-secondary">Confirmar Contraseña</label>
                  <input
                    type={showNewPasswordText ? 'text' : 'password'}
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    placeholder="Repite la contraseña"
                    className="w-full px-2.5 py-1.5 bg-surface border border-border rounded-lg text-xs"
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end pt-1">
                <Button type="submit" size="sm" disabled={creating}>
                  {creating ? <Loader2 size={13} className="animate-spin mr-1" /> : <Check size={13} />}
                  <span>Guardar y Crear Acceso</span>
                </Button>
              </div>
            </form>
          )}

          {/* Lista de Usuarios */}
          <div className="space-y-2 pt-1">
            {loadingUsers ? (
              <div className="py-6 text-center text-xs text-text-muted flex items-center justify-center gap-2">
                <Loader2 size={14} className="animate-spin text-brand-primary" />
                <span>Cargando usuarios...</span>
              </div>
            ) : users.length === 0 ? (
              <div className="py-4 text-center text-xs text-text-muted">
                No se encontraron usuarios adicionales.
              </div>
            ) : (
              <div className="divide-y divide-border border border-border rounded-xl overflow-hidden">
                {users.map((u) => {
                  const isSelf = u.username === currentUser?.username;
                  const isResettingThis = resetTargetUser === u.username;

                  return (
                    <div key={u.id} className="p-3 bg-surface hover:bg-surface-secondary/40 transition-colors space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className={clsx(
                            'w-7 h-7 rounded-lg flex items-center justify-center shrink-0 text-xs font-bold',
                            u.role === 'admin' ? 'bg-brand-primary/10 text-brand-primary' : 'bg-slate-100 dark:bg-slate-800 text-text-secondary'
                          )}>
                            {u.role === 'admin' ? <ShieldCheck size={14} /> : <UserCheck size={14} />}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-xs font-semibold text-text-primary truncate">{u.name}</span>
                              <span className="text-[10px] font-mono text-text-muted">(@{u.username})</span>
                              <span className={clsx(
                                'text-[9px] font-semibold px-1.5 py-0.2 rounded-full uppercase tracking-wider',
                                u.role === 'admin'
                                  ? 'bg-brand-primary/15 text-brand-primary'
                                  : 'bg-surface-secondary text-text-muted border border-border'
                              )}>
                                {u.role}
                              </span>
                              {isSelf && (
                                <span className="text-[9px] bg-state-confirmed/15 text-state-confirmed px-1.5 py-0.2 rounded-full font-medium">
                                  Tú
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => {
                              if (isResettingThis) {
                                setResetTargetUser(null);
                                setResetPasswordVal('');
                                setConfirmResetPasswordVal('');
                              } else {
                                setResetTargetUser(u.username);
                                setResetPasswordVal('');
                                setConfirmResetPasswordVal('');
                              }
                            }}
                            className="p-1.5 text-text-secondary hover:text-brand-primary rounded-lg text-xs font-medium flex items-center gap-1 hover:bg-surface-secondary cursor-pointer transition-colors"
                            title="Cambiar contraseña"
                          >
                            <KeyRound size={13} />
                            <span className="hidden sm:inline text-[11px]">Restablecer Clave</span>
                          </button>

                          {!isSelf && (
                            <button
                              type="button"
                              onClick={() => handleDeleteUser(u.username)}
                              className="p-1.5 text-text-muted hover:text-state-cancelled rounded-lg text-xs hover:bg-state-cancelled/10 cursor-pointer transition-colors"
                              title="Eliminar usuario"
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Sub-formulario de Reset Password */}
                      {isResettingThis && (
                        <div className="bg-surface-secondary p-3 rounded-lg border border-border space-y-2 animate-fade-in">
                          <div className="flex items-center justify-between text-xs font-medium text-text-primary">
                            <span>Nueva contraseña para @{u.username}</span>
                            <button
                              type="button"
                              onClick={() => setShowResetPasswordText(!showResetPasswordText)}
                              className="text-[10px] text-text-muted hover:text-text-primary flex items-center gap-0.5"
                            >
                              {showResetPasswordText ? <EyeOff size={11} /> : <Eye size={11} />}
                              <span>{showResetPasswordText ? 'Ocultar' : 'Ver'}</span>
                            </button>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <input
                              type={showResetPasswordText ? 'text' : 'password'}
                              value={resetPasswordVal}
                              onChange={(e) => setResetPasswordVal(e.target.value)}
                              placeholder="Nueva contraseña (mín. 4)"
                              className="w-full px-2.5 py-1.5 bg-surface border border-border rounded text-xs"
                              autoFocus
                            />
                            <input
                              type={showResetPasswordText ? 'text' : 'password'}
                              value={confirmResetPasswordVal}
                              onChange={(e) => setConfirmResetPasswordVal(e.target.value)}
                              placeholder="Confirmar nueva contraseña"
                              className="w-full px-2.5 py-1.5 bg-surface border border-border rounded text-xs"
                            />
                          </div>

                          <div className="flex justify-end gap-2 pt-1">
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              onClick={() => {
                                setResetTargetUser(null);
                                setResetPasswordVal('');
                                setConfirmResetPasswordVal('');
                              }}
                            >
                              Cancelar
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              disabled={resetting || !resetPasswordVal || !confirmResetPasswordVal}
                              onClick={() => handleResetPassword(u.username)}
                            >
                              {resetting ? <Loader2 size={12} className="animate-spin" /> : 'Guardar Nueva Clave'}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-surface border border-border rounded-xl p-4 flex items-center gap-3 text-xs text-text-muted">
          <AlertCircle size={16} className="text-brand-primary shrink-0" />
          <span>La creación y gestión de accesos de personal está reservada para el Administrador.</span>
        </div>
      )}

      {/* 2. SECCIÓN: CÓDIGO DE RESCATE MAESTRO (SOLO ADMIN) */}
      {currentUser?.role === 'admin' && (
        <div className="bg-surface border border-border rounded-xl p-4 space-y-3 shadow-xs">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 text-amber-500 flex items-center justify-center shrink-0">
              <ShieldAlert size={16} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-text-primary">Código de Rescate Maestro</h3>
              <p className="text-[11px] text-text-muted">
                Úsalo para recuperar el acceso de Administrador si alguna vez olvidas tu contraseña
              </p>
            </div>
          </div>

          <div className="p-3 bg-surface-secondary border border-border rounded-xl space-y-2.5">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-medium text-text-secondary">Código Actual:</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowRecoveryKey(!showRecoveryKey)}
                  className="text-[11px] text-text-muted hover:text-text-primary flex items-center gap-1 cursor-pointer"
                >
                  {showRecoveryKey ? <EyeOff size={12} /> : <Eye size={12} />}
                  <span>{showRecoveryKey ? 'Ocultar' : 'Ver'}</span>
                </button>
                <button
                  type="button"
                  onClick={handleCopyRecoveryKey}
                  disabled={!recoveryKey}
                  className="text-[11px] text-brand-primary hover:underline flex items-center gap-1 font-medium cursor-pointer"
                >
                  {copiedRecoveryKey ? <Check size={12} /> : <Copy size={12} />}
                  <span>{copiedRecoveryKey ? 'Copiado' : 'Copiar'}</span>
                </button>
              </div>
            </div>

            <div className="p-2.5 bg-surface border border-border rounded-lg font-mono text-xs font-bold text-text-primary tracking-wider select-all break-all">
              {loadingRecoveryKey ? (
                <span className="text-text-muted font-normal">Cargando código...</span>
              ) : recoveryKey ? (
                showRecoveryKey ? recoveryKey : '•'.repeat(24)
              ) : (
                'FRESHA-RESCUE-ADMIN-2026'
              )}
            </div>

            <div className="flex items-center justify-between pt-1">
              <span className="text-[10px] text-text-muted">
                Guarda este código en un gestor de contraseñas o lugar seguro.
              </span>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={regeneratingKey}
                onClick={handleRegenerateRecoveryKey}
                className="text-xs"
              >
                {regeneratingKey ? <Loader2 size={12} className="animate-spin mr-1" /> : <Sparkles size={12} />}
                <span>Regenerar Nuevo</span>
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 3. SECCIÓN: CAMBIAR MI PROPIA CONTRASEÑA */}
      <div className="bg-surface border border-border rounded-xl p-4 space-y-4 shadow-xs">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-brand-primary/10 text-brand-primary flex items-center justify-center shrink-0">
            <Lock size={16} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-text-primary">Mi Contraseña</h3>
            <p className="text-[11px] text-text-muted">
              Actualiza la contraseña de tu cuenta activa (@{currentUser?.username})
            </p>
          </div>
        </div>

        <form onSubmit={handleChangeSelfPassword} className="space-y-3 max-w-md">
          <div className="space-y-1">
            <label className="text-[11px] font-medium text-text-secondary">Contraseña Actual</label>
            <input
              type="password"
              value={currentPass}
              onChange={(e) => setCurrentPass(e.target.value)}
              placeholder="Ingresa tu contraseña actual"
              className="w-full px-3 py-2 bg-surface-secondary border border-border rounded-lg text-xs"
              required
            />
          </div>

          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="text-[11px] font-medium text-text-secondary">Nueva Contraseña</label>
              <button
                type="button"
                onClick={() => setShowSelfPassText(!showSelfPassText)}
                className="text-[10px] text-text-muted hover:text-text-primary flex items-center gap-0.5"
              >
                {showSelfPassText ? <EyeOff size={11} /> : <Eye size={11} />}
                <span>{showSelfPassText ? 'Ocultar' : 'Ver'}</span>
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                type={showSelfPassText ? 'text' : 'password'}
                value={newSelfPass}
                onChange={(e) => setNewSelfPass(e.target.value)}
                placeholder="Mínimo 4 caracteres"
                className="w-full px-3 py-2 bg-surface-secondary border border-border rounded-lg text-xs"
                required
              />
              <input
                type={showSelfPassText ? 'text' : 'password'}
                value={confirmSelfPass}
                onChange={(e) => setConfirmSelfPass(e.target.value)}
                placeholder="Confirmar nueva"
                className="w-full px-3 py-2 bg-surface-secondary border border-border rounded-lg text-xs"
                required
              />
            </div>
          </div>

          <div className="pt-1">
            <Button type="submit" size="sm" disabled={changingSelfPass || !currentPass || !newSelfPass}>
              {changingSelfPass ? (
                <>
                  <Loader2 size={13} className="animate-spin mr-1.5" />
                  Actualizando...
                </>
              ) : (
                <>
                  <KeyRound size={13} />
                  <span>Actualizar Mi Contraseña</span>
                </>
              )}
            </Button>
          </div>
        </form>
      </div>

      {/* Confirm Modal for User Deletion */}
      <ConfirmModal
        isOpen={!!userToDelete}
        title="Eliminar Acceso de Usuario"
        message={`¿Estás seguro de que deseas eliminar permanentemente el acceso para '@${userToDelete}'? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar Acceso"
        cancelLabel="Cancelar"
        variant="danger"
        loading={deletingUser}
        onConfirm={confirmDeleteUser}
        onCancel={() => !deletingUser && setUserToDelete(null)}
      />

      {/* Confirm Modal for Master Recovery Key Regeneration */}
      <ConfirmModal
        isOpen={showRegenerateConfirm}
        title="Regenerar Código de Rescate Maestro"
        message="¿Deseas generar un nuevo Código de Rescate Maestro? El código anterior dejará de ser válido de inmediato."
        confirmLabel="Regenerar Código"
        cancelLabel="Cancelar"
        variant="danger"
        loading={regeneratingKey}
        onConfirm={confirmRegenerateRecoveryKey}
        onCancel={() => !regeneratingKey && setShowRegenerateConfirm(false)}
      />
    </div>
  );
}
